import { Mic, MicOff, Phone } from "lucide-react";
import { LiveAudioVisualizer } from "react-audio-visualize";
//@ts-ignore;
import BrandGIF from "../../assets/profile-2.gif";
import ProfileImage from "../../assets/new.png";
import CFALogo from "@/assets/Cfa.png";
import CfaAgent from "@/assets/cfaAgent.png";

interface VoiceCallInterfaceProps {
  isCalling:boolean,
  isMuted: boolean;
  setIsMuted: (muted: boolean) => void;
  agentName: string;
  toggleMute: () => void;
  onEndCall: () => void;
  onChat: () => void;
  mediaRecorder: MediaRecorder | null;
  icon?: string;
}

const VoiceCallInterface: React.FC<VoiceCallInterfaceProps> = ({
  isCalling,
  isMuted,
  setIsMuted,
  toggleMute,
  agentName,
  onEndCall,
  onChat,
  mediaRecorder,
  icon
}) => {
  
  const showCallEndAlert = () => {
    alert(`Your call with ${agentName} has ended`);
  };

  return (
    <>
      {/* Google Fonts */}
      <link
        href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Manrope:wght@300;400;500;600&display=swap"
        rel="stylesheet"
      />

      <div className="bg-black-950/40 p-2  rounded-[48px]">
        <div
          className="my-4 sm:m-4 sm:pt-6 sm:pb-8 w-[80vw] md:w-[350px] h-[50vh] sm:h-[600px] min-h-[400px] text-black flex flex-col relative overflow-hidden rounded-[2rem] sm:rounded-[3rem] border border-rose-100"
          style={{
            background: "white",
            fontFamily: "Manrope, sans-serif"
          }}
        >
          <div
            className="absolute bottom-0 left-0 right-0 pointer-events-none z-0"
            style={{
              height: "30%",
              background:
                "linear-gradient(to top, rgba(255, 184, 192, 0.8) 0%, rgba(254, 205, 211, 0.4) 50%, transparent 100%)"
            }}
          />

          {/* CFA Logo at the top */}
          <div className="flex-shrink-0 pt-2 sm:pt-3 text-center z-10 mb-2 sm:mb-4">
            <img
              src={CFALogo}
              alt="CFA Logo"
              className="mx-auto  h-20 sm:h-32 w-auto"
            />
          </div>
          
          <div className="flex-1 flex flex-col items-center justify-center relative z-10 min-h-0 py-2">
            <div className="relative mb-2 sm:mb-4 md:mb-8">
              <div className="w-32 h-32 sm:w-40 sm:h-40 md:w-48 md:h-48 lg:w-56 lg:h-56 relative">
                {/* Outer circle with radial gradient, border and enhanced custom shadow */}
                <div
                  className="absolute inset-0 rounded-full border-2 sm:border-2 md:border-3 border-white"
                  style={{
                    background:
                      "radial-gradient(circle, rgba(251, 113, 133, 0.6) 0%, rgba(251, 113, 133, 0.3) 50%, rgba(255, 255, 255, 0.8) 100%)",
                    boxShadow:
                      "0 25px 50px rgba(227, 233, 248, 0.8), 0 15px 30px rgba(227, 233, 248, 0.6), 0 8px 20px rgba(227, 233, 248, 0.4), 0 4px 10px rgba(251, 113, 133, 0.3)"
                  }}
                ></div>

                {/* Middle circle for additional depth */}
                <div
                  className="absolute inset-1 sm:inset-1.5 md:inset-2 lg:inset-2.5 rounded-full"
                  style={{
                    background:
                      "radial-gradient(circle, rgba(251, 113, 133, 0.4) 0%, rgba(251, 113, 133, 0.2) 40%, rgba(255, 255, 255, 0.9) 100%)"
                  }}
                ></div>

                {/* Profile image with 3D shadow and rose-200 border */}
                <div
                  className="absolute inset-4 sm:inset-10 rounded-full overflow-hidden bg-white z-10 border-2 border-rose-200"
                  style={{
                    boxShadow:
                      "0 8px 16px rgba(0, 0, 0, 0.12), 0 3px 8px rgba(0, 0, 0, 0.08), inset 0 1px 2px rgba(255, 255, 255, 0.8)"
                  }}
                >
                  <img
                    src={icon ==="https://d3udl6i7sipqjw.cloudfront.net/agent-images/cfa_image.png" ? "https://d3udl6i7sipqjw.cloudfront.net/agent-images/cfa_image.png" : CfaAgent}
                    alt="Agent"
                    className="w-full h-full object-cover"
                  />
                </div>

                {/* Subtle inner glow */}
                <div
                  className="absolute inset-4 sm:inset-5 md:inset-6 lg:inset-7 rounded-full z-20 pointer-events-none"
                  style={{
                    boxShadow: "inset 0 0 8px rgba(251, 113, 133, 0.1)"
                  }}
                ></div>
              </div>
            </div>
          </div>

          {/* Controls - Fixed at bottom with proper spacing */}
          <div className="flex-shrink-0 pb-3 sm:pb-4 md:pb-8 px-4 sm:px-8 z-10">
            {/* Waveform */}
            <div className="sound-visualizer-wrapper mb-3 sm:mb-4 flex justify-center">
              {mediaRecorder && !isMuted && isCalling ? (
                <div style={{ display: "flex", gap: "3px" }}>
                  <div style={{ transform: "scaleX(-1)" }}>
                    <LiveAudioVisualizer
                      mediaRecorder={mediaRecorder}
                      width={80}
                      height={50}
                      barWidth={2}
                      gap={2}
                      barColor={"#6B7280"}
                      fftSize={256}
                      minDecibels={-70}
                      smoothingTimeConstant={0.9}
                    />
                  </div>
                  <div>
                    <LiveAudioVisualizer
                      mediaRecorder={mediaRecorder}
                      width={120}
                      height={50}
                      barWidth={2}
                      gap={2}
                      barColor={"#6B7280"}
                      fftSize={256}
                      minDecibels={-70}
                      smoothingTimeConstant={0.9}
                    />
                  </div>
                </div>
              ) : (
                // Placeholder bars when muted or no mediaRecorder
                <div
                  style={{
                    width: "203px",
                    height: "50px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    opacity: 0.3
                  }}
                >
                  <div
                    style={{ display: "flex", gap: "2px", alignItems: "end" }}
                  >
                    {Array.from({ length: 30 }, (_, i) => (
                      <div
                        key={i}
                        style={{
                          width: "2px",
                          height: "8px",
                          backgroundColor: "#000000",
                          opacity: 0.3
                        }}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Buttons */}
            <div className="flex mt-16 justify-center space-x-4 sm:space-x-8">
              {/* Mute button */}
              <button
                onClick={toggleMute}
                className={`flex items-center justify-center gap-5 px-4 py-2 rounded-[44px] border-[0.5px] transition-all duration-300 md:w-[55px] md:h-[50px] hover:scale-110 hover:shadow-lg ${
                  isMuted ? "border-red-300" : "border-[#F3F4F6]"
                }`}
                style={{
                  width: "55px",
                  height: "40px",
                  background: isMuted
                    ? "linear-gradient(180deg, #FEE2E2 0%, #FECACA 32.51%, #FCA5A5 100%)"
                    : "linear-gradient(180deg, #F9FAFB 0%, #F3F4F6 32.51%, #D1D5DB 100%)",
                  boxShadow: "0px 12px 16px 0px #20202033"
                }}
              >
                {isMuted ? (
                  <MicOff size={20} className="text-red-500" />
                ) : (
                  <Mic size={20} className="text-gray-500" />
                )}
              </button>
              {/* End call button */}
              <button
                onClick={onEndCall}
                className="flex items-center justify-center gap-5 px-4 py-2 rounded-[44px] transition-all duration-300 hover:scale-110 hover:shadow-lg"
                style={{
                  width: "55px",
                  height: "40px",
                  background:
                    "linear-gradient(180deg, #F87171 0%, #EF4444 32.51%, #DC2626 100%)",
                  boxShadow: "0px 12px 16px 0px #20202033"
                }}
              >
                <Phone size={20} className="text-white rotate-[135deg]" />
              </button>
            </div>
          </div>

          <style>{`@keyframes spin { from { transform: rotate(0deg) } to { transform: rotate(360deg) } }`}</style>
        </div>
      </div>
    </>
  );
};

export default VoiceCallInterface;
